import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  signal,
  ViewChild,
} from "@angular/core";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { IDetailCellRendererParams } from "ag-grid-community";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { CouponData } from "@bitwarden/web-vault/app/models/interfaces/coupons.interface";
import { MOCK_COUPONS } from "@bitwarden/web-vault/app/gloss/coupons/coupon.data";

const GROCERY_COUPON_IDS = ["hellofresh", "everyplate", "youfoodz"];

const COUPON_DISPLAY_META: Record<string, { initials: string; colorClass: string }> = {
  hellofresh: {
    initials: "HF",
    colorClass:
      "tw-text-color-money-in-text tw-bg-color-money-in-bg tw-border-color-money-in-border",
  },
  everyplate: {
    initials: "EP",
    colorClass:
      "tw-text-color-money-out-text tw-bg-color-money-out-bg tw-border-color-money-out-border",
  },
  youfoodz: {
    initials: "YF",
    colorClass:
      "tw-text-color-accent-future-text tw-bg-color-accent-future-bg tw-border-color-accent-future-border",
  },
};

@Component({
  selector: "app-transaction-info-panel",
  templateUrl: "./transaction-info-panel.component.html",
  host: { class: "tw-block tw-h-full" },
})
export class TransactionInfoPanelComponent
  implements ICellRendererAngularComp, AfterViewInit, OnDestroy
{
  private dialogFactory = new DialogFactory();
  coupons: CouponData[] = MOCK_COUPONS.filter((c) => GROCERY_COUPON_IDS.includes(c.id));

  @ViewChild("couponsScroll") private couponsScrollRef?: ElementRef<HTMLElement>;
  protected canScrollCouponsLeft = signal(false);
  protected canScrollCouponsRight = signal(false);
  private resizeObserver?: ResizeObserver;
  private ngZone = inject(NgZone);
  private scrollRafId?: number;

  agInit(_params: IDetailCellRendererParams): void {}

  refresh(_params: IDetailCellRendererParams): boolean {
    return false;
  }

  ngAfterViewInit(): void {
    // The ag-grid detail row measures and resizes itself after this view is first rendered, so
    // the container has no real width yet at this point - observe it directly rather than
    // relying on a one-shot check or window resize events.
    const el = this.couponsScrollRef?.nativeElement;
    if (el) {
      // ResizeObserver callbacks aren't zone-patched in this app, so re-enter the Angular zone
      // to make sure the signal update here actually triggers a re-render.
      this.resizeObserver = new ResizeObserver(() =>
        this.ngZone.run(() => this.updateCouponsScrollArrows()),
      );
      this.resizeObserver.observe(el);
      // Outside Angular and passive: it runs on every wheel tick, never cancels the event, and needs no change detection.
      this.ngZone.runOutsideAngular(() =>
        el.addEventListener("wheel", this.onCouponsWheel, { passive: true }),
      );
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.couponsScrollRef?.nativeElement.removeEventListener("wheel", this.onCouponsWheel);
    if (this.scrollRafId !== undefined) {
      cancelAnimationFrame(this.scrollRafId);
    }
  }

  // Coalesces rapid-fire scroll events into a single getBoundingClientRect() read per frame,
  // instead of forcing layout on every scroll tick.
  protected onCouponsScroll(): void {
    if (this.scrollRafId !== undefined) {
      return;
    }
    this.scrollRafId = requestAnimationFrame(() => {
      this.scrollRafId = undefined;
      this.updateCouponsScrollArrows();
    });
  }

  // The grid's full-width rows call preventDefault() on sideways wheel / trackpad input (to scroll the grid,
  // which here never scrolls sideways), so the carousel never got it. Keep a sideways gesture from reaching
  // the grid; vertical ones still bubble so the page scrolls as usual. Shift+wheel counts as sideways, the
  // same test the grid uses, since some browsers send it as deltaY.
  private readonly onCouponsWheel = (event: WheelEvent): void => {
    if (event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      event.stopPropagation();
    }
  };

  protected updateCouponsScrollArrows(): void {
    const el = this.couponsScrollRef?.nativeElement;
    const firstCard = el?.firstElementChild as HTMLElement | undefined;
    const lastCard = el?.lastElementChild as HTMLElement | undefined;
    if (!el || !firstCard || !lastCard) {
      return;
    }
    // Compare the first/last card's actual position to the container's visible edges rather
    // than scrollWidth/scrollLeft - the container's own tw-px-2 padding counts as extra
    // scrollable space, which left a ~8px dead zone at both ends where those would never
    // report "fully scrolled" even once every card was already in view.
    const containerRect = el.getBoundingClientRect();
    const firstRect = firstCard.getBoundingClientRect();
    const lastRect = lastCard.getBoundingClientRect();
    this.canScrollCouponsLeft.set(firstRect.left < containerRect.left - 1);
    this.canScrollCouponsRight.set(lastRect.right > containerRect.right + 1);
  }

  protected scrollCoupons(direction: 1 | -1): void {
    const el = this.couponsScrollRef?.nativeElement;
    if (!el) {
      return;
    }
    // Scroll by one full card (+ gap) rather than a fixed guess, so a click always lands on a
    // card boundary instead of leaving the next one half-cut-off.
    const card = el.firstElementChild as HTMLElement | null;
    const gap = parseFloat(getComputedStyle(el).columnGap || "0");
    const step = card ? card.offsetWidth + gap : el.clientWidth;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  }

  displayMeta(id: string): { initials: string; colorClass: string } {
    return COUPON_DISPLAY_META[id] ?? { initials: id.slice(0, 2).toUpperCase(), colorClass: "" };
  }

  onViewCoupon(coupon: CouponData): void {
    this.dialogFactory.openCouponDetailsDialog(coupon);
  }
}
